import { Card, CardContent, Grid, Typography } from '@mui/material';
import axios from 'axios';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { AutocompleteInput, Datagrid, FunctionField, List, ReferenceInput, SelectInput } from 'react-admin';
import constants from '../../constants';
import MyPagination from '../MyPagination';

type Kpis = {
	sent: number;
	emailClicked: number;
	pushOpened: number;
	contentClicked: number;
	meaningful: number;
	ignored: number;
} | null;

const KpiStrip = () => {
	const [kpis, setKpis] = useState<Kpis>(null);
	useEffect(() => {
		axios
			.get(`${constants.API_URL}interventions/admin/list`, {
				params: { limit: 1 },
				headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
			})
			.then((res) => setKpis(res.data?.kpis ?? null))
			.catch(() => setKpis(null));
	}, []);
	const items = [
		{ label: 'נשלחו', value: kpis?.sent },
		{ label: 'קליק במייל', value: kpis?.emailClicked },
		{ label: 'פתחו פוש', value: kpis?.pushOpened },
		{ label: 'קליק על תוכן', value: kpis?.contentClicked },
		{ label: 'פעולה משמעותית', value: kpis?.meaningful },
		{ label: 'התעלמו', value: kpis?.ignored },
	];
	return (
		<Grid container spacing={2} style={{ marginBottom: 8 }}>
			{items.map(({ label, value }) => (
				<Grid item xs={4} sm={2} key={label}>
					<Card>
						<CardContent style={{ textAlign: 'center', padding: 12 }}>
							<Typography variant="h5">{value ?? '-'}</Typography>
							<Typography variant="body2" color="textSecondary">
								{label}
							</Typography>
						</CardContent>
					</Card>
				</Grid>
			))}
		</Grid>
	);
};

const CONTENT_TYPE_LABELS: Record<string, string> = { boost: 'בוסט', vod: 'VOD', lecture: 'הרצאה' };

const statusOf = (record: any) => {
	if (record?.decision === 'NO_SEND') return record?.decision_reason?.replace('NO_SEND_', '') ?? 'לא נשלח';
	if (record?.meaningful_action_at) return '✅ פעולה משמעותית';
	if (record?.content_clicked_at) return '👆 קליק על תוכן';
	if (record?.email_clicked_at || record?.push_opened_at) return '📬 נפתח';
	if (record?.ignored_at) return '😴 התעלמות';
	return 'נשלח';
};

const InterventionsList = () => {
	return (
		<>
			<KpiStrip />
			<List
				resource="interventions"
				perPage={20}
				pagination={<MyPagination />}
				sort={{ field: 'createdAt', order: 'DESC' }}
				title="התערבויות פרואקטיביות"
				filters={[
					<ReferenceInput source="company" reference="company" perPage={1000} label="חברה" alwaysOn>
						<AutocompleteInput optionText="english_name" label="חברה" filterToQuery={(q: string) => ({ english_name: q })} />
					</ReferenceInput>,
					<SelectInput
						source="decision"
						label="החלטה"
						alwaysOn
						choices={[
							{ id: 'SENT', name: 'נשלח' },
							{ id: 'NO_SEND', name: 'לא נשלח' },
						]}
					/>,
					<SelectInput
						source="channel"
						label="ערוץ"
						alwaysOn
						choices={[
							{ id: 'email', name: 'מייל' },
							{ id: 'push', name: 'פוש' },
						]}
					/>,
				]}
			>
				<Datagrid bulkActionButtons={false} rowClick={false}>
					<FunctionField
						label="תאריך"
						render={(r: any) => (r?.createdAt ? moment(r.createdAt).format('DD/MM/YY HH:mm') : '-')}
					/>
					<FunctionField
						label="משתמש"
						render={(r: any) =>
							r?.member ? `${r.member.first_name ?? ''} ${r.member.last_name ?? ''}`.trim() || r.member.email : '-'
						}
					/>
					<FunctionField label="חברה" render={(r: any) => r?.company?.english_name ?? '-'} />
					<FunctionField label="סגמנט" render={(r: any) => r?.engagement_segment ?? '-'} />
					<FunctionField
						label="תוכן"
						render={(r: any) =>
							r?.content?.content_type
								? `${CONTENT_TYPE_LABELS[r.content.content_type] ?? r.content.content_type}: ${r.content.title ?? ''}`
								: '-'
						}
					/>
					<FunctionField label="צורך" render={(r: any) => r?.selected_need ?? '-'} />
					<FunctionField
						label="ציון"
						render={(r: any) =>
							r?.intervention_score != null ? `${r.intervention_score} / סף ${r.threshold ?? '-'}` : '-'
						}
					/>
					<FunctionField label="ערוצים" render={(r: any) => (r?.channels?.length ? r.channels.join(' + ') : '-')} />
					<FunctionField label="סטטוס" render={(r: any) => statusOf(r)} />
				</Datagrid>
			</List>
		</>
	);
};

export default InterventionsList;
